/**
 * The undelivered catch-up path (TRD section 6). The Relay stores nothing, so
 * a producer parks every storable event in its own Postgres and the client
 * collects what it did not take live:
 *
 *   GET /api/relay/undelivered?client=desktop|web   (core-api, member bearer)
 *       RelayUndeliveredQuery -> RelayUndeliveredResponse
 *
 * The producer writes ahead of the delivery attempt, never after it:
 *
 *   1. Store the envelope for every surface the topic serves
 *      (`relayWriteAheadSurfaces`), before calling publish.
 *   2. Publish. The Worker delivers to live sockets and waits for acks.
 *   3. Delete the rows for the surfaces in `acked`.
 *
 * Storing only after a failed attempt leaves a gap: a client that reconnects
 * while an attempt to its old socket is still waiting for its ack timeout gets
 * `hello`, runs its one catch-up fetch before the producer has stored
 * anything, and then misses the event until its next reconnect. Writing ahead
 * means the row exists before any socket can be sent the frame, so a fetch at
 * any moment sees it, and a producer that crashes mid-attempt leaves the row
 * in place instead of losing the event. The cost is a client sometimes getting
 * one event both live and from the fetch, which it already handles by
 * deduplicating on `eventId`.
 *
 * The route returns and deletes the caller's rows for that surface in one
 * transaction, oldest first. A client calls it once right after its socket's
 * `hello`, and on `RELAY_FALLBACK_POLL_INTERVAL_SECONDS` while it has no
 * socket at all. The envelopes are the same shape the socket carries, so one
 * code path applies both, deduplicating by `eventId`.
 *
 * Only topics whose registry entry says `whenUndelivered: "store"` can appear
 * here. A Reset offer is never stored: an offer nobody acknowledged live is
 * `dispatch_failed`, because a prompt must never arrive through a path that
 * did not deliver it within its window.
 */
import { z } from "zod";
import { RelayEnvelope } from "./envelope.js";
import { RELAY_TOPICS, RelaySurface } from "./topics.js";
/**
 * How long an undelivered row is kept before the retention sweep deletes it.
 * A client away longer than this reloads the feed instead of trusting the
 * fetch (`relayNeedsFullReload`).
 */
export const RELAY_UNDELIVERED_RETENTION_HOURS = 24;
/** How often a client without a socket polls the undelivered route. */
export const RELAY_FALLBACK_POLL_INTERVAL_SECONDS = 30;
/**
 * The surfaces a producer stores an envelope for before publishing it (step 1
 * above): every surface the topic serves, or none for a topic that is never
 * stored. After the publish, the producer deletes the rows for `acked`.
 */
export function relayWriteAheadSurfaces(topic) {
    const entry = RELAY_TOPICS[topic];
    return entry.whenUndelivered === "store" ? entry.surfaces : [];
}
export const RelayUndeliveredQuery = z.strictObject({ client: RelaySurface });
export const RelayUndeliveredResponse = z
    .strictObject({ events: z.array(RelayEnvelope) })
    .refine((value) => value.events.every((event) => RELAY_TOPICS[event.topic].whenUndelivered === "store"), { message: "only storable topics are ever undelivered", path: ["events"] })
    .refine((value) => value.events.every((event, index) => index === 0 ||
    Date.parse(value.events[index - 1]?.createdAt ?? "") <= Date.parse(event.createdAt)), { message: "undelivered events are oldest first", path: ["events"] })
    .refine((value) => new Set(value.events.map((event) => event.eventId)).size === value.events.length, {
    message: "an event is undelivered at most once",
    path: ["events"],
});
/**
 * Whether a reconnecting client must reload the whole feed instead of trusting
 * the undelivered fetch. Rows older than the retention window are gone, so a
 * client that has not synced within it (or never has) cannot know what it
 * missed. `lastSyncAt` is the client's own record of its last live frame or
 * successful undelivered fetch.
 */
export function relayNeedsFullReload(lastSyncAt, now = new Date()) {
    if (lastSyncAt === null)
        return true;
    const age = Date.parse(typeof now === "string" ? now : now.toISOString()) - Date.parse(lastSyncAt);
    return Number.isNaN(age) || age >= RELAY_UNDELIVERED_RETENTION_HOURS * 60 * 60 * 1000;
}
//# sourceMappingURL=undelivered.js.map